/* @flow */

import { parsePoints } from './points';

/**
 * Calcula el total de puntos en el carrito basado en los productos
 * @param {Object} cart - Objeto del carrito
 * @param {Object} products - Lista de productos con sus datos
 * @returns {number} Total de puntos en el carrito
 */
export function calculateCartPointsTotal(
  cart: Object,
  products: Object,
): number {
  let totalPoints = 0;

  if (!cart || Object.keys(cart).length === 0) {
    return 0;
  }

  const productsList = products?.list || [];

  Object.keys(cart).forEach((productId) => {
    const product = productsList.find((p) => p.id === productId);
    if (!product || !product.data) return;

    const productData = product.data;
    const cartItem = cart[productId];

    // Si el producto tiene variantes
    if (productData.variables && productData.variables.length > 0) {
      Object.keys(cartItem).forEach((variantName) => {
        if (variantName === 'quantity') return; // Skip quantity key

        const variant = productData.variables.find(
          (v) => v.name === variantName,
        );
        if (variant && variant.points) {
          const quantity = cartItem[variantName]?.quantity || 0;
          totalPoints += parsePoints(variant.points) * quantity;
        }
      });
    } else {
      // Producto sin variantes
      if (productData.points) {
        const quantity = cartItem.quantity || 0;
        totalPoints += parsePoints(productData.points) * quantity;
      }
    }
  });

  return totalPoints;
}

/**
 * Calcula los puntos que se necesitarían para agregar un producto al carrito
 * @param {Object} product - Producto a agregar
 * @param {Object} variant - Variante seleccionada (opcional)
 * @param {number} quantity - Cantidad a agregar
 * @returns {number} Puntos necesarios para este producto
 */
export function calculateProductPoints(
  product: Object,
  variant: ?Object,
  quantity: number,
): number {
  if (!product) return 0;

  let productPoints = 0;

  if (variant && variant.points) {
    productPoints = parsePoints(variant.points);
  } else if (product.points) {
    productPoints = parsePoints(product.points);
  }

  return productPoints * quantity;
}

/**
 * Verifica si un usuario tiene suficientes puntos para agregar un producto
 * @param {Object} user - Usuario actual
 * @param {Object} product - Producto a agregar
 * @param {Object} cart - Carrito actual
 * @param {Object} products - Lista de productos
 * @param {Object} variant - Variante seleccionada (opcional)
 * @param {number} quantity - Cantidad a agregar
 * @returns {Object} { isValid: boolean, message: string }
 */
export function validatePointsForProduct(
  user: Object,
  product: Object,
  cart: Object,
  products: Object,
  variant: ?Object,
  quantity: number,
): { isValid: boolean, message: string } {
  if (!user || !user.points) {
    return {
      isValid: true, // Si no tiene puntos definidos, permitir (puede pagar con otro método)
      message: '',
    };
  }

  // Obtener puntos del producto a agregar
  const productPoints = calculateProductPoints(product, variant, quantity);

  // Si el producto no se paga con puntos, permitir
  if (productPoints === 0) {
    return {
      isValid: true,
      message: '',
    };
  }

  const userPoints = parsePoints(user.points);
  const currentCartPoints = calculateCartPointsTotal(cart, products);
  const totalPointsNeeded = currentCartPoints + productPoints;

  if (totalPointsNeeded > userPoints) {
    const pointsShortage = totalPointsNeeded - userPoints;
    return {
      isValid: false,
      message: `No tienes suficientes puntos para canjear este producto .`,
    };
  }

  return {
    isValid: true,
    message: '',
  };
}

export default {
  calculateCartPointsTotal,
  calculateProductPoints,
  validatePointsForProduct,
};
